import { useCallback, useEffect, useState } from "react";
import API from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tag, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { money } from "../lib/pricing";

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");

/** Win-backs grouped by first-month discount: which % actually converts, and a one-click way to make it the default offer. */
export default function WinbackResultsCard({ onDefaultChanged }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const load = useCallback(() => API.get("/platform/payment-recovery/winback-results").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setDefault = async (pct) => {
    setSaving(pct);
    try {
      await API.put("/platform/payment-recovery/settings", { winback_default_offer_pct: pct });
      toast.success(pct ? `Win-back emails now offer ${pct}% off the first month by default` : "Win-back emails no longer pre-select a discount");
      load(); onDefaultChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); } finally { setSaving(null); }
  };

  if (!data) return null;
  const rows = data.buckets || [];
  return (
    <div className="border-t border-border" data-testid="winback-results">
      <div className="px-4 py-2 bg-muted/20 flex flex-wrap items-center gap-2">
        <Tag size={13} className="text-muted-foreground" />
        <p className="text-[10px] font-semibold text-muted-foreground">WIN-BACK RESULTS BY OFFER</p>
        <span className="text-[10px] text-muted-foreground" data-testid="winback-results-totals">{data.total_sent} sent · {data.total_converted} came back</span>
        {data.best_pct !== null && data.best_pct !== undefined && <Badge variant="outline" className="text-[9px] gap-1 border-emerald-400 text-emerald-700" data-testid="winback-results-best"><Trophy size={10} /> Best: {data.best_pct ? `${data.best_pct}% off` : "no offer"}</Badge>}
        <span className="ml-auto text-[10px] text-muted-foreground" data-testid="winback-results-default">Default offer: {data.default_pct ? `${data.default_pct}% off` : "none (box unticked)"}</span>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-2.5 text-xs text-muted-foreground" data-testid="winback-results-empty">No win-back emails sent yet — results by discount appear here once you've sent a few.</p>
      ) : (
        <table className="w-full text-xs" data-testid="winback-results-table">
          <thead><tr className="text-[10px] text-muted-foreground text-left"><th className="px-4 py-1 font-medium">Offer</th><th className="px-2 py-1 font-medium text-right">Sent</th><th className="px-2 py-1 font-medium text-right">Came back</th><th className="px-2 py-1 font-medium text-right">Conversion</th><th className="px-2 py-1 font-medium text-right">Avg days</th><th className="px-2 py-1 font-medium text-right">Codes used</th><th className="px-2 py-1 font-medium text-right">MRR won</th><th className="px-4 py-1 font-medium text-right">Last sent</th><th className="px-4 py-1" /></tr></thead>
          <tbody className="divide-y divide-border/60">
            {rows.map((r) => (
              <tr key={r.pct} className={r.pct === data.best_pct ? "bg-emerald-50/50 dark:bg-emerald-950/20" : ""} data-testid={`winback-results-row-${r.pct}`}>
                <td className="px-4 py-1.5 font-medium">{r.label}{r.pct === data.default_pct && <Badge variant="secondary" className="ml-1.5 text-[9px]" data-testid={`winback-results-default-badge-${r.pct}`}>default</Badge>}</td>
                <td className="px-2 py-1.5 text-right">{r.sent}</td>
                <td className="px-2 py-1.5 text-right">{r.converted}</td>
                <td className="px-2 py-1.5 text-right font-semibold" data-testid={`winback-results-conv-${r.pct}`}>{r.conversion_pct === null ? "—" : `${r.conversion_pct}%`}</td>
                <td className="px-2 py-1.5 text-right">{r.avg_days_to_convert ?? "—"}</td>
                <td className="px-2 py-1.5 text-right">{r.pct ? r.redeemed : "—"}</td>
                <td className="px-2 py-1.5 text-right">{money(r.mrr_won, { compact: true })}<span className="text-[10px] text-muted-foreground font-normal">/mo</span></td>
                <td className="px-4 py-1.5 text-right text-muted-foreground">{fmtDay(r.last_sent_at)}</td>
                <td className="px-4 py-1.5 text-right">
                  {r.pct !== data.default_pct && <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" disabled={saving !== null} onClick={() => setDefault(r.pct)} data-testid={`winback-results-set-default-${r.pct}`}>{saving === r.pct ? "Saving…" : "Set as default"}</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
